import React from 'react';
import * as Sentry from '@sentry/react';
import {useParams} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {useDocumentSelfieDetection, useScanDocumentKiosko} from '../../../../hooks';
import {IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE} from '@chekin/core';
import {useSummary} from '../../../../context/summary';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {
  getDocumentNationality,
  getDocumentType,
  useIVStore,
  PhotoBox,
} from '@chekin/shared';
import backSidePlaceholder from '../../../../assets/img/backside-placeholder.svg?no-inline';
import {KioskoErrorModal} from 'components/common/KioskoErrorModal';
import {HelperText} from '../styled';

function BackScanView() {
  const {uploadType} = useParams();
  const {reservationId, countryCode, data: summary} = useSummary();
  const {t} = useTranslation();
  const layoutMode = useLayoutMode();
  const docType = IV_DOCUMENT_IMAGE_TYPES.back;

  const documentType = useIVStore(getDocumentType);
  const documentNationality = useIVStore(getDocumentNationality);

  const isUpload = uploadType === 'upload';
  const isScan = uploadType === 'scan';

  const {
    ProcessModal,
    ErrorModal,
    RetryModal,
    isProcessing,
    sendBackSideDocument,
  } = useDocumentSelfieDetection({docType});

  const getHelperText = () => {
    if (isUpload) {
      return <Trans i18nKey="upload_photo_of_back_side_of_your_document" />;
    } else {
      return <Trans i18nKey="now_back_side_of_your_document_expanded" />;
    }
  };

  const handleChangePhoto = React.useCallback(
    ({image, source}: {image: string; source: IV_PHOTO_SOURCE}) => {
      if (countryCode) {
        sendBackSideDocument({
          image,
          source,
          countryCode,
          reservationId,
          documentType: documentType?.value,
          nationality: documentNationality,
        });
      } else {
        console.error('countryCode is missing', summary);
        Sentry.captureMessage('countryCode is missing', {extra: {summary}});
      }
    },
    [
      countryCode,
      documentType?.value,
      reservationId,
      sendBackSideDocument,
      documentNationality,
      summary,
    ],
  );

  const {scanDocument: scanDocumentKiosko} = useScanDocumentKiosko({
    onDocumentScanned: data => {
      handleChangePhoto({
        image: data?.image_base64 || '',
        source: IV_PHOTO_SOURCE.appCamera,
      });
    },
  });

  const handleScanDocument = React.useCallback(() => {
    return scanDocumentKiosko({side: IV_DOCUMENT_IMAGE_TYPES.back});
  }, [scanDocumentKiosko]);

  return (
    <>
      <HelperText className="iv-back-view__helper">{getHelperText()}</HelperText>
      {!isProcessing && (
        <PhotoBox
          mobileHelperTitle={t('scan_your_document')}
          mobileHelperText={<Trans i18nKey="now_back_side_of_your_document_expanded" />}
          onUploadPhoto={handleChangePhoto}
          placeholderImage={backSidePlaceholder}
          isUpload={isUpload}
          isScan={isScan}
          onScanDocument={handleScanDocument}
          layoutMode={layoutMode}
        />
      )}
      <ProcessModal />
      <ErrorModal />
      <RetryModal />
      <KioskoErrorModal />
    </>
  );
}

export {BackScanView};
